import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import {
  allAutocompleteThunk,
  allSuggestionsThunk,
  clearEnhancedSearchAutoComplete,
  clearSuggestions,
  combinedSearchThunk,
  loadSearchMetaData,
  selectAutoCompleteTextPlus,
  selectSuggestions,
  selectTopicQuery,
} from "../features/enhancedSearch";
import { saveRecentSearches } from "../features/enhancedSearch/recentSearchService";
import { resetStateForSearch } from "../features/counter/counterSlice";

function shouldWeShowSuggestionText(suggestedText, searchValue) {
  return (
    suggestedText !== "" &&
    searchValue.length > 3 &&
    `${suggestedText}`.startsWith(searchValue) &&
    suggestedText !== searchValue
  );
}

/**
 * Custom hook that encapsulates all common search functionality
 * Used by both ModernSearch and HeadSearch components
 */
export const useSearchLogic = (options = {}) => {
  const {
    onAppModeRedirect = null, // callback for app mode redirect
    debounceDelay = 800,
    minSearchLength = 2,
    minAutocompleteLength = 3,
  } = options;

  const push = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const dropdownRef = useRef(null);
  const inputFieldRef = useRef(null);
  const prevSearchValueRef = useRef("");

  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showSavedOrRecent, setShowSavedOrRecent] = useState(false);
  const [showSuggestion, setShowSuggestion] = useState(false);

  const suggestionsSelector = useSelector(selectSuggestions);
  const suggestedText = useSelector(selectAutoCompleteTextPlus);
  const previousSearchQuery = useSelector(selectTopicQuery);

  const showSuggestedText = shouldWeShowSuggestionText(suggestedText, searchValue);

  const fetchAutocompleteAndSuggestion = useDebouncedCallback((searchTerm, isAutoComplete) => {
    const trimmedValue = searchTerm.trim().toLowerCase();
    if (trimmedValue && trimmedValue !== "") {
      dispatch(allSuggestionsThunk({ searchText: trimmedValue }));
      if (!isAutoComplete) {
        dispatch(allAutocompleteThunk({ searchText: trimmedValue }));
      }
      setIsOpen(true);
      setShowSavedOrRecent(false);
      setShowSuggestion(true);
    }
  }, debounceDelay);

  const handleInputChange = (e) => {
    setIsOpen(true);
    const newValue = e.target.value;
    setSearchValue(newValue);

    if (newValue === "") {
      setShowSuggestion(false);
      setShowSavedOrRecent(true);
      return;
    }

    if (newValue !== prevSearchValueRef.current) {
      setShowSuggestion(false);
      setShowSavedOrRecent(false);

      if (newValue.length >= minAutocompleteLength) {
        fetchAutocompleteAndSuggestion(newValue, false);
      }
    }
  };

  const handleFocus = () => {
    // Allow custom app mode redirect logic
    if (onAppModeRedirect) {
      onAppModeRedirect();
      return;
    }

    setIsOpen(true);
    setShowSavedOrRecent(true);

    if (searchValue === prevSearchValueRef.current && searchValue.trim()) {
      setShowSavedOrRecent(false);
    }
  };

  const handleBlur = () => {
    prevSearchValueRef.current = searchValue;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchValue.length >= minSearchLength) {
      executeSearch();
    }
    if ((e.key === "Tab" || e.keyCode === 9) && searchValue.length >= minAutocompleteLength) {
      if (suggestedText && suggestedText !== searchValue) {
        e.preventDefault();
        setSearchValue(suggestedText);
        fetchAutocompleteAndSuggestion(suggestedText, true);
        setIsOpen(true);
      }
    } else {
      fetchAutocompleteAndSuggestion.cancel();
    }
  };

  const executeSearch = () => {
    const spellCorrection =
      suggestedText &&
      suggestedText.toLowerCase() !== searchValue.toLowerCase() &&
      suggestedText.toLowerCase().startsWith(searchValue.toLowerCase())
        ? suggestedText
        : null;
    fetchAutocompleteAndSuggestion.cancel();
    dispatch(clearEnhancedSearchAutoComplete());
    dispatch(clearSuggestions());
    dispatch(
      combinedSearchThunk({
        searchText: searchValue,
        top: previousSearchQuery?.top,
      }),
    );
    push(
      `/searchAction/?appendedinputbuttons=${encodeURIComponent(searchValue)}${spellCorrection ? `&correction=${encodeURIComponent(spellCorrection)}` : ""}`,
    );

    inputFieldRef.current?.blur();
    setIsOpen(false);
  };

  const suggestionItemClick = (item) => {
    try {
      saveRecentSearches("combinedSearch", {
        searchText: item.suggestion,
        url: item.url,
      });
      dispatch(resetStateForSearch());

      setTimeout(() => {
        push(item.url);
        inputFieldRef.current?.blur();
      }, 50);
    } catch (error) {
      console.error(error);
    }
    setIsOpen(false);
  };

  const clearSearch = () => {
    setSearchValue("");
    setIsOpen(false);
    setShowSavedOrRecent(false);
    setShowSuggestion(false);
    inputFieldRef.current?.blur();
  };

  useEffect(() => {
    dispatch(loadSearchMetaData());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        event.target !== inputFieldRef.current
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("appendedinputbuttons")) {
      const initialValue = searchParams.get("appendedinputbuttons");
      setSearchValue(initialValue);
      prevSearchValueRef.current = initialValue;
    }
  }, [location.search]);

  return {
    // Refs
    dropdownRef,
    inputFieldRef,

    // State
    isOpen,
    searchValue,
    showSavedOrRecent,
    showSuggestion,
    showSuggestedText,

    // Selectors
    suggestionsSelector,
    suggestedText,

    // Handlers
    handleInputChange,
    handleFocus,
    handleBlur,
    handleKeyDown,
    executeSearch,
    suggestionItemClick,
    clearSearch,
    setIsOpen,
    setSearchValue,

    // Utilities
    fetchAutocompleteAndSuggestion,
  };
};
